/**
 * `P0-UIK-008`: FCFA, formatted once for the whole product. Section 06, 6.5.2.
 *
 * **FCFA is an integer currency with no subdivision.** No decimals, ever, and
 * a thousands separator that is a **narrow no-break space, U+202F**, per
 * `DECISION ADDED 06-16`: the mockups' `frNum` splits with a plain space, and
 * a plain space lets `1 500 000` break across a line so a reader sees `1 500`
 * at the end of one line and `000` at the start of the next. A price that can
 * be misread by a factor of a thousand is not a formatting preference.
 *
 * **EVERY space inside a formatted price is U+202F** , `DECISION ADDED 06-35`,
 * the round-42 ruling. 6.5.2 contradicted itself: its `Symbol` row said a
 * normal space before `FCFA`, its own snippet used the narrow no-break space.
 * The snippet wins, and the reasoning is `06-16`'s own, carried one step
 * further: a formatted price is ONE UNBREAKABLE TOKEN. `1 500 000` stranded
 * at the end of a line with `FCFA` wrapped below it is the same misreading
 * family as `1 500` above `000`, and one law , every space in a price is
 * U+202F , is enforceable with a single codepoint assert, where two different
 * space characters is where the next drift breeds.
 *
 * **Strict in what we emit, liberal in what we accept**: `parseMoney` still
 * takes U+202F, U+00A0 and U+0020, so a price typed or pasted by a human, or
 * written under the old reading, still reads back.
 *
 * **AMENDED BY RECORD, `MONEY-SPACE`, 22 October 2026 (the architect's ruling on the designer's advice; the
 * text above is kept as written).** Money keeps U+202F between the digit groups and puts a FULL no-break
 * space, **U+00A0**, before `FCFA`: `5 100 000 FCFA`. The reason `06-35` gave for one codepoint ("a price is one
 * unbreakable token") still holds, because U+00A0 does not break either; what changes is the width of the gap
 * before the unit, which the designer wants as wide as a word space and not as thin as a group. Platform-wide,
 * because CSS cannot scope a codepoint. The law is now two codepoints: U+202F inside the number, U+00A0 before
 * the unit, nothing else.
 */

/**
 * U+202F, the narrow no-break space, written as an ESCAPE and never as the
 * character. `no-irregular-whitespace` caught the literal, and it was right:
 * an invisible character in source is one nobody can review, and a stray
 * no-break space pasted beside it would look identical and behave differently.
 */
export const NNBSP = "\u202F";

/**
 * U+00A0, the no-break space, written as an ESCAPE for the reason `NNBSP` is: the gap between the number and
 * `FCFA`, since `MONEY-SPACE` (22 October 2026).
 */
export const NBSP = "\u00A0";

/** The three spaces `parseMoney` tolerates: U+202F, U+00A0, U+0020. */
const ACCEPTED_SPACES = /[\u202F\u00A0\u0020]/gu;

/**
 * `1 500 000 FCFA` (U+202F between the groups, U+00A0 before the unit), and `- 35 000 FCFA` for a balance movement. Zero is
 * `0 FCFA`, never an empty string and never a dash , an empty cell reads as
 * "unknown", and a dash reads as "not applicable", and a balance of zero is
 * neither.
 */
export function formatMoney(amount: number): string {
  if (!Number.isFinite(amount)) {
    // A screen would render `NaN FCFA` and a reader would believe it was a
    // number. Naming the value is the only honest answer.
    throw new Error(`formatMoney received a non-finite amount: ${String(amount)}`);
  }
  const whole = Math.trunc(Math.abs(amount));
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/gu, NNBSP);
  const sign = amount < 0 ? "- " : "";
  return `${sign}${grouped}${NBSP}FCFA`;
}

/**
 * `S1-a` COMPORTEMENT DU COMPTE (the architect's ruling of 1 November, item 2): **a movement, or a balance that can be negative, with the TRUE minus U+2212 attached to its figure** (`−20 000 FCFA`), where
 * `formatMoney` writes the keyboard's hyphen and a space (`- 20 000 FCFA`, the form every other screen has kept). A credit is `formatMoney`'s own text, zero carries no sign. `parseMoney` reads it back
 * (it accepts U+2212). Added beside `formatMoney`, which is untouched.
 */
export function formatMovement(amount: number): string {
  if (!Number.isFinite(amount)) {
    throw new Error(`formatMovement received a non-finite amount: ${String(amount)}`);
  }
  return amount < 0 ? `\u2212${formatMoney(Math.abs(amount))}` : formatMoney(amount);
}

/**
 * `P0-UIK-031`, the founder's walk of 6 September (`R65`): **a count, grouped
 * like a price and carrying no currency.**
 *
 * The walk read « 62 400 FCFA km » in a car's header and in its list row: the
 * mileage had been passed through `formatMoney`, which appends the currency
 * because that is its whole job. A kilometre count is not money.
 *
 * **What it shares with a price, and why.** The separator is U+202F, by
 * `DECISION ADDED 06-16`'s own reasoning carried across: a mileage split at a
 * line break shows `62` above `400` and misreads by a factor of a thousand
 * exactly as a price does. The law is about the number, not about the currency.
 *
 * **What it does not share.** No `FCFA`, and no unit at all , the unit is the
 * screen's word, in the screen's language, beside the number. Baking `km` in
 * here would make this function unusable for the seat count and the photo
 * count, which are the next two callers.
 *
 * A non-finite count renders as the empty string, on `formatMoney`'s own rule:
 * `NaN km` on a listing reads as a measurement somebody took.
 */
export function formatQuantity(count: number): string {
  if (!Number.isFinite(count)) return "";
  const whole = Math.trunc(count);
  const sign = whole < 0 ? "-" : "";
  const digits = Math.abs(whole).toString();
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    groups.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return sign + groups.join(NNBSP);
}

/**
 * `P0-UIK-032`, the founder's walk of 6 September (`R69`): **a plate, rendered
 * as the record spells it.**
 *
 * The founder typed « BB 1042 MD » and the page answered « BB1042MD ». The
 * normalisation is load-bearing and stays , `ux_vehicles__plate_live` is what
 * stops one physical car being for sale twice, and it can only work on a single
 * spelling , but it is a STORAGE rule. The mockup writes the plate with its
 * spaces, and a person checking a car against its papers reads the spaced form.
 *
 * The grouping is Mali's own: two letters, four digits, two letters. A value
 * that does not fit is returned UNCHANGED rather than forced , a plate from
 * another country, or one this function has not been taught, must render as
 * itself and not as a guess with spaces in the wrong places.
 */
export function formatPlate(plate: string): string {
  const bare = plate.replaceAll(/\s/gu, "").toUpperCase();
  const match = /^([A-Z]{2})(\d{4})([A-Z]{2})$/u.exec(bare);
  return match === null ? plate : `${match[1] ?? ""} ${match[2] ?? ""} ${match[3] ?? ""}`;
}

/**
 * The inverse, tolerant on input and strict on output.
 *
 * It accepts every space a human or a copy-paste can produce , the narrow
 * no-break space this module writes, the no-break space a spreadsheet writes,
 * and the plain space a person types , because a client typing an amount into
 * a field is not thinking about Unicode. `C12` of 6.9 requires the round trip.
 */
export function parseMoney(text: string): number {
  const cleaned = text
    .replace(/FCFA/giu, "")
    .replace(ACCEPTED_SPACES, "")
    .replace(/\u2212/gu, "-") // U+2212 MINUS SIGN, which some keyboards produce
    .trim();
  if (cleaned === "" || !/^-?\d+$/u.test(cleaned)) {
    throw new Error(`parseMoney could not read an integer amount from: ${text}`);
  }
  return Number.parseInt(cleaned, 10);
}
